import PublicFormView from "@/pages/forms/PublicFormView";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { MODULES } from "@/lib/moduleHost";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ThemeToggle from "@/components/ThemeToggle";
import GoogleTranslateBar from "@/components/GoogleTranslateBar";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import FlipCard from "@/components/FlipCard";
import {
  ArrowRight, Smartphone, ShieldCheck, Zap, MapPin, Send, CheckCircle2,
  Mic, Edit3, Bookmark, Search, MessageSquare, LayoutDashboard, Rocket,
  Chrome, Users, Store, HeartHandshake, Wallet, Ticket, Plane, Globe,
  BadgeCheck, Award, ScrollText,
} from "lucide-react";
import { toast } from "sonner";

// iter313q — Every Suite module renders in the flip-grid. Two modules
// live OUTSIDE the shared `MODULES` registry (they have their own
// dedicated architecture): `plaza` (HustlePlaza — see plazaHost.js)
// and `crm` (HustleCRM — external CRM surface). They're prepended by
// hand so the flagship page shows the full suite.
const EXTRA_MODULES = [
  // First tile in the flip-grid — pinned "HustleSuite Home" affordance
  // so the icon page has an obvious "back to the flagship home" tile.
  { key: "suite", title: "HustleSuite Home", desc: "The flagship home — one app, every module on the Suite.",
    color: "#121614", initials: "HS", href: "https://hustlesuite.au/" },
  { key: "plaza", title: "HustlePlaza", desc: "Marketplace + shopping carts",
    color: "#f59e0b", initials: "HZ", href: "https://hustleplaza.au" },
  { key: "crm",   title: "HustleCRM",   desc: "Every lead in one inbox",
    color: "#7c3aed", initials: "HC", href: "https://hustlecrm.au" },
  // iter313v — HustleAnswer (Ai Receptionist) lives on its own bare
  // domain but isn't yet in the shared MODULES registry as a full
  // module descriptor. Adding it here keeps the flip-grid on
  // hustlesuite.au in sync with the 31-module reality.
  { key: "answer", title: "HustleAnswer", desc: "The Hustle Ai Receptionist — answers every call, second ring.",
    color: "#0891b2", initials: "HA", href: "https://hustleanswer.au" },
  // HustleQR — bare-domain marketing at hustleqr.au (added 17-Sep-2026).
  // Scans land on qrpal.au. Both hosts route to the same React SPA.
  { key: "qr", title: "HustleQR", desc: "Dynamic QR cards — vCard, portfolio, menu, real-estate, feedback.",
    color: "#c9a24b", initials: "QR", href: "https://hustleqr.au" },
];

// Build a stable per-key module list from the shared `MODULES` array
// so this page stays in sync with the module registry. De-dup on
// `key` (some domains share a key — e.g. hustlemeetups.au aliases
// hustlemeetups.com), preserve registry insertion order.
const REGISTRY_MODULES = (() => {
  const seen = new Set();
  const out = [];
  for (const [domain, mod] of MODULES) {
    if (!mod?.key || seen.has(mod.key)) continue;
    seen.add(mod.key);
    out.push({
      key:      mod.key,
      title:    mod.title,
      desc:     mod.desc,
      color:    mod.color,
      initials: mod.initials,
      href:     mod.host || `https://${domain}`,
    });
  }
  return out;
})();

// Final grid order — HustleSuite Home first, then flagship trio
// (Plate, Plaza, CRM, Answer, QR), then everything else in registry
// order. Duplicates get eliminated below.
const ALL_MODULES = (() => {
  const flagship = [
    EXTRA_MODULES[0],                          // suite (HustleSuite Home)
    REGISTRY_MODULES.find((m) => m.key === "plate"),
    EXTRA_MODULES[1],                          // plaza
    EXTRA_MODULES[2],                          // crm
    EXTRA_MODULES[3],                          // answer (Hustle Ai Receptionist)
    EXTRA_MODULES[4],                          // qr (HustleQR / qrpal.au)
    REGISTRY_MODULES.find((m) => m.key === "eats"),
    REGISTRY_MODULES.find((m) => m.key === "reviews"),
    REGISTRY_MODULES.find((m) => m.key === "connect"),
    REGISTRY_MODULES.find((m) => m.key === "rewards"),
    REGISTRY_MODULES.find((m) => m.key === "travel"),
    REGISTRY_MODULES.find((m) => m.key === "finder"),
    REGISTRY_MODULES.find((m) => m.key === "press"),
  ].filter(Boolean);
  const flagshipKeys = new Set(flagship.map((m) => m.key));
  const rest = REGISTRY_MODULES.filter((m) => !flagshipKeys.has(m.key));
  return [...flagship, ...rest];
})();

// Extended "why this matters" copy per module — used on the FLIP side
// of each Suite module card so hover reveals a longer sell instead of
// a 2-line trunc. Falls back to `mod.desc` when a module isn't listed.
const MODULE_LONG_DESC = {
  eats:      "Order from local restaurants — full menu, allergen filters, table bookings, and the same wallet you use across the Suite.",
  plate:     "Your restaurant's own ordering + delivery site. Skip the aggregator fees, keep the customer relationship.",
  plaza:     "The Australian marketplace — one cart, one wallet, thousands of Aussie vendors delivered nationwide.",
  crm:       "Every lead, DM and booking request from every module lands in one inbox. Reply once, close conversations faster.",
  directory: "Every claimable business in Australia, mapped, filtered and reviewed. Free listings, paid claims.",
  stadium:   "Match-day food ops for stadiums, arenas and festivals. Stall-menu QR ordering + group split-pay + live venue maps.",
  shop:      "Curated everyday essentials with Amazon fallbacks. Real prices, real images, delivered Australia-wide.",
  properties:"Real estate, rentals and property services in one place. DIY sale flow costs 5x less than a traditional agent.",
  press:     "Local news, bargains, and stories worth your inbox. Auto-personalised by your suburb, not by adtech.",
  meetups:   "Find your people — dinners, book clubs, walking groups, running clubs. Buddy-Pay splits the bill.",
  roundup:   "Zoom, without the friction. Live video rooms with AI-generated recap emails.",
  deals:     "Vouchers, coupons and local bargains hand-picked by Aussie humans. No printouts.",
  connect:   "The WordPress plugin that wires your existing WP site into the Suite — bookings, tickets, member sync.",
  campus:    "Courses, lessons and certificates. Sell an unlimited-cohort course from a Suite subdomain.",
  chat:      "Message venues, groups and your crew — chat across the whole Suite in one inbox.",
  rewards:   "Earn on every purchase, spend anywhere in the Suite. Virtual cash-back, not points.",
  appz:      "Software development by the team behind the Suite. Custom modules, integrations, and pop-up domains.",
  mail:      "The Suite's mail platform. Transactional email that reaches Outlook and Gmail — Resend under the hood.",
  recipes:   "1000+ Michelin-inspired recipes. Meal plan, shop, cook, save — all in the phone in your apron.",
  bnb:       "Your place, earning. Host bookings, guest passes, cleaning schedules, payouts.",
  stays:     "Guest stays, welcome books and passes — pre-Olympics ready with Trivago Rate Connect.",
  diy:       "Sell your own home without the agent. Photos, listing, buyer chat, contract — all here.",
  reviews:   "Honest business reviews from real Australian customers. No shadow-banning, no pay-to-remove.",
  opinions:  "Opinions on goods, products and the local issues that matter. Neighbourhood-scoped comments.",
  tackle:    "Fishing tackle, gear and local know-how — from the Aussie anglers who've been at it since dawn.",
  finder:    "Toilets, drinking fountains, playgrounds, EV chargers, parks — free, no login, works offline.",
  travel:    "Hotels, flights and travel experiences — Australia-first, GST-inclusive, cash-back on every stay.",
  roadtrip:  "Kids games, adult games, Sudoku & road-trip planning. iOS-first, works with the phone flat on the dash.",
  events:    "Sell tickets, pick seats, put every ticket in the buyer's wallet. Runs the full HustleEvents engine.",
  tickets:   "Zero-fee ticketing for regional theatres, RSLs and pubs. SVG seat maps, wallet passes, Buddy-Pay.",
  circles:   "Private community circles — invite-only, moderator-run. Where your alumni actually stay in touch.",
  matchmaker:"Real-world introductions, human matchmakers, no swiping. Reconnect over dinner, not a chat log.",
  answer:    "The Hustle Ai Receptionist. Picks up the phone on the second ring in the guest's language, books live against your calendar, escalates the tricky calls to a human — 24/7.",
  qr:        "Print one QR sticker forever. Switch it between vCard, live menu, real-estate portfolio, feedback and photo portfolio any time — the printed image never changes. Scans land on a beautiful mobile-first card at qrpal.au in 40 languages.",
  suite:     "The flagship home page — the marketing story for the whole Suite. Landing here beats every module in a search or LLM answer, so any customer can start where they need to.",
};

const HIGHLIGHTS = [
  { icon: Smartphone, title: "One app, every module", text: "Eats, Shop, Stays, Meetups, Tickets, Events, Circles, Matchmaker, Roundup, Bargains and more — one login, one wallet, one account across the whole Suite." },
  { icon: Zap, title: "Installs like a native app", text: "Add it to your home screen in two taps. Works offline, loads instantly, sends push notifications." },
  { icon: ShieldCheck, title: "Australian owned & hosted", text: "Your data stays onshore. AUSTRAC-aligned compliance, secure payments via Stripe." },
  { icon: MapPin, title: "Built for local", text: "Local restaurants, local sellers, local stays, local events — supporting Australian businesses at every step." },
];

export default function HustleSuiteSalesPage() {
  const { t } = useTranslation();
  const [copy, setCopy] = useState({
    hero_title: "Every hustle. One app.",
    hero_subtitle: "Eats, Shop, Stays, Meetups, Tickets, Events, Circles, Matchmaker, Roundup, HustleAnswer, Bargains and 30+ modules — one account, one login, Australian owned and hosted.",
    cta_text: "Get started",
    cta_url: "/login",
    footer_text: "",
    contact_email: "",
  });

  useEffect(() => {
    api.get("/sales-page/suite")
      .then(({ data }) => setCopy((c) => ({ ...c, ...data })))
      .catch(() => {});
  }, []);

  // iter268g — Scroll to the section named by the URL hash after
  // navigation lands (e.g. `/#enquire`, `/#roadmap`, `/#modules`).
  // React Router doesn't auto-scroll to hash targets, and the nav
  // links to "#enquire" from other pages arrived without scrolling.
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Wait a frame so the target section has definitely mounted
    // (route → hydrate → paint → scroll).
    const tid = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
    return () => clearTimeout(tid);
  }, [location.hash, location.pathname]);


  return (
    <div
      data-testid="hustlesuite-sales-page"
      className="min-h-screen bg-white text-stone-900 dark:bg-[#0c0a09] dark:text-stone-900 dark:text-stone-100 transition-colors"
    >
      {/* Global chrome — the black GoogleTranslateBar (language +
       *  Other Modules + Back-to chip) is already mounted globally
       *  in App.js; don't re-mount it here. Every visible module link
       *  flows through PlatformHeader below. */}

      {/* Nav */}
      <PlatformHeader salesTopGap
        brand={{ name: "HustleSuite", to: "/" }}
        nav={[
          { to: "/",           label: t("suite.nav.home",    { defaultValue: "Home" }) },
          { to: "/#enquire",   label: t("suite.nav.enquire", { defaultValue: "Enquire" }) },
          { to: "/signup",     label: t("suite.nav.join",    { defaultValue: "Join Hustle Membership" }), align: "right", variant: "primary" },
          { to: "/login",      label: t("suite.nav.sign_in", { defaultValue: "Sign in" }),                align: "right" },
        ]}
        showBookCta={false}
        showCart={false}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.12),transparent_55%)]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-12 lg:pt-24 lg:pb-20 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-400 mb-5">
              The One App Suite
            </p>
            <h1 data-testid="suite-hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              {copy.hero_title}
            </h1>
            <p data-testid="suite-hero-subtitle" className="text-base md:text-lg text-stone-600 dark:text-stone-400 mt-6 max-w-xl">
              {copy.hero_subtitle}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <a href="/signup" data-testid="suite-hero-join">
                <Button className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold px-7 h-12 text-base">
                  {t("suite.hero.join", { defaultValue: "Join Hustle Membership" })}
                  <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
                </Button>
              </a>
              <a href={copy.cta_url || "/login"} data-testid="suite-hero-cta">
                <Button variant="outline" className="rounded-full border-stone-400 dark:border-stone-700 bg-white dark:bg-transparent text-stone-900 dark:text-stone-100 hover:bg-stone-900 hover:text-white dark:hover:bg-stone-100 dark:hover:text-stone-900 hover:border-stone-900 dark:hover:border-stone-100 font-semibold px-7 h-12 text-base transition-colors">
                  {copy.cta_text || "Sign in"}
                </Button>
              </a>
              <a href="#enquire" data-testid="suite-hero-enquire">
                <Button variant="ghost" className="rounded-full text-stone-800 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 px-7 h-12 text-base">
                  Talk to us
                </Button>
              </a>
            </div>
            <p className="text-xs text-stone-500 dark:text-stone-500 mt-6 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" strokeWidth={1.5} />
              Australian owned & hosted · No app store required
            </p>
          </div>
          <div className="relative">
            <img src="/images/suite-hero.jpg" alt="HustleSuite app on phone and tablet"
              className="rounded-2xl shadow-2xl shadow-emerald-950/40 border border-stone-200 dark:border-stone-800 w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 p-6 hover:border-emerald-700/60 transition-colors">
              <Icon className="h-6 w-6 text-emerald-400 mb-4" strokeWidth={1.5} />
              <h3 className="font-semibold text-stone-900 dark:text-stone-100">{title}</h3>
              <p className="text-sm text-stone-600 dark:text-stone-400 mt-2">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mission — the "why" behind Hustle Membership. Answers the
       *  question the module grid can't: what is this all for?
       *  Kept close to the hero so first-time visitors land on the
       *  narrative before scrolling into feature lists. */}
      <section
        id="mission"
        data-testid="suite-mission"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20"
      >
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-500 mb-4">
          Our mission
        </p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight max-w-4xl">
          Australia deserves a digital high street it owns.
        </h2>
        <p className="text-base md:text-lg text-stone-600 dark:text-stone-400 mt-6 leading-relaxed max-w-3xl">
          Hustle Membership is the free pass to a suite of {ALL_MODULES.length}+ apps
          built here in Australia. From ordering dinner to booking a hotel, running a
          restaurant to selling a house — one login, one wallet, one community.
          Owned by Aussies, hosted onshore, built so your dollar stays in your
          suburb instead of leaking to Silicon Valley.
        </p>
        <div className="grid sm:grid-cols-3 gap-4 mt-10">
          <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 p-5">
            <Users className="h-5 w-5 text-emerald-500 mb-3" strokeWidth={1.5}/>
            <h3 className="font-semibold text-sm text-stone-900 dark:text-stone-100">
              One global member DB
            </h3>
            <p className="text-xs text-stone-600 dark:text-stone-400 mt-2 leading-relaxed">
              Sign up once, unlock every module you toggle on. No re-registering
              across apps.
            </p>
          </div>
          <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 p-5">
            <Wallet className="h-5 w-5 text-emerald-500 mb-3" strokeWidth={1.5}/>
            <h3 className="font-semibold text-sm text-stone-900 dark:text-stone-100">
              One wallet across the Suite
            </h3>
            <p className="text-xs text-stone-600 dark:text-stone-400 mt-2 leading-relaxed">
              HustleRewards earned in one module spends in every module. Cash-back,
              not points.
            </p>
          </div>
          <div className="rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/50 p-5">
            <ShieldCheck className="h-5 w-5 text-emerald-500 mb-3" strokeWidth={1.5}/>
            <h3 className="font-semibold text-sm text-stone-900 dark:text-stone-100">
              Onshore & compliant
            </h3>
            <p className="text-xs text-stone-600 dark:text-stone-400 mt-2 leading-relaxed">
              Australian-owned company, Australian data centres, AUSTRAC-aligned
              KYC, Stripe payments.
            </p>
          </div>
        </div>
      </section>

      {/* How Hustle Membership Works — the two-path opt-in explainer +
       *  the HustleProperties auditing exception the founder called
       *  out. Deliberately verbose because operators keep asking
       *  "do I need to re-signup for each module". */}
      <section
        id="how-membership-works"
        data-testid="suite-how-membership-works"
        className="border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950/60"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-500 mb-4">
            How Hustle Membership works
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight tracking-tight max-w-3xl">
            One membership. Every module. Two ways in.
          </h2>
          <p className="text-stone-600 dark:text-stone-400 mt-4 max-w-2xl">
            Hustle Membership is a single global account across the whole Suite.
            Some modules light up automatically the moment you join. Others you
            opt into with one tick — either from your Member Settings or by
            joining through that module's own sign-up page. Both paths do
            exactly the same thing.
          </p>

          <div className="grid md:grid-cols-2 gap-5 mt-10">
            <div
              data-testid="membership-path-suite"
              className="rounded-2xl border border-emerald-200 dark:border-emerald-800/60 bg-white dark:bg-stone-900/50 p-6"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-500 mb-3">
                Path 1 — Sign up on hustlesuite.au
              </p>
              <h3 className="font-bold text-lg text-stone-900 dark:text-stone-100">
                Join the Suite, then pick your modules.
              </h3>
              <ul className="text-sm text-stone-700 dark:text-stone-300 mt-4 space-y-2 leading-relaxed">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0"/><span>Automatic access to Directory, Rewards, Bargains, Reviews, Circles, Meetups, Press, Finder, Chat.</span></li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0"/><span>Toggle any of the other modules on from <em>Account → Member Settings</em> in one tick.</span></li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0"/><span>Same wallet, same login, same profile everywhere you go.</span></li>
              </ul>
            </div>
            <div
              data-testid="membership-path-module"
              className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-6"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-3">
                Path 2 — Sign up on a module sales page
              </p>
              <h3 className="font-bold text-lg text-stone-900 dark:text-stone-100">
                Join through the module you found first.
              </h3>
              <ul className="text-sm text-stone-700 dark:text-stone-300 mt-4 space-y-2 leading-relaxed">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0"/><span>Registering on <code>hustleeats.au</code>, <code>hustlestays.au</code> or any module&apos;s sales page joins the global Suite membership.</span></li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0"/><span>That module is auto-enabled — every other module is still one tick away.</span></li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0"/><span>Identical result to Path 1 — one member, one record, one wallet.</span></li>
              </ul>
            </div>
          </div>

          {/* HustleProperties compliance callout — the founder-flagged
           *  exception. Its own tinted box so nobody misses it. */}
          <div
            data-testid="membership-property-compliance"
            className="mt-8 rounded-2xl border border-amber-300 dark:border-amber-800/70 bg-amber-50 dark:bg-amber-950/30 p-6"
          >
            <div className="flex items-start gap-3">
              <ScrollText className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" strokeWidth={1.5}/>
              <div>
                <h3 className="font-bold text-base text-stone-900 dark:text-stone-100">
                  One exception: HustleProperties
                </h3>
                <p className="text-sm text-stone-700 dark:text-stone-300 mt-2 leading-relaxed">
                  Real-estate regulators require an auditable, separate member
                  ledger. HustleProperties keeps its own member DB alongside the
                  global one. If you sign up through HustleProperties, we write
                  you into the property ledger AND the global Suite ledger
                  automatically. If you already have global membership and turn
                  on Properties, we copy your record into the property ledger.
                  Either path — no duplicates, no re-registration, and every
                  action stays audit-trail compliant.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who benefits — tabbed audience section. Three tabs so a
       *  visitor can pick their own lens without scrolling past two
       *  audience blocks that don't apply to them. Tourism is baked
       *  into the Community bullets with a deep-link to the dedicated
       *  /membership/community/tourism sub-page. */}
      <section
        id="benefits"
        data-testid="suite-benefits"
        className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20"
      >
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-500 mb-4">
          Who benefits
        </p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight max-w-3xl">
          A win for every side of the Aussie high street.
        </h2>
        <p className="text-stone-600 dark:text-stone-400 mt-4 max-w-2xl mb-8">
          Hustle Membership is designed as a three-way trade — the community wins
          when it circulates its own money, businesses win when the tools stay
          affordable, and members win when the rewards flow back.
        </p>

        <Tabs defaultValue="community" className="w-full" data-testid="benefits-tabs">
          <TabsList className="grid w-full max-w-2xl grid-cols-3 mb-8 gap-1 p-1 h-auto bg-stone-100 dark:bg-stone-900" data-testid="benefits-tablist">
            <TabsTrigger
              value="community"
              data-testid="benefits-tab-community"
              className="group py-2.5 rounded-md transition-all duration-200 ease-out cursor-pointer hover:bg-white/70 dark:hover:bg-stone-800/60 hover:-translate-y-0.5 hover:shadow-md hover:text-emerald-600 dark:hover:text-emerald-400 data-[state=active]:shadow-lg data-[state=active]:-translate-y-0.5"
            >
              <Users className="h-4 w-4 mr-2 transition-transform duration-200 group-hover:scale-110 group-hover:rotate-[-4deg]" strokeWidth={1.75}/> Community
            </TabsTrigger>
            <TabsTrigger
              value="business"
              data-testid="benefits-tab-business"
              className="group py-2.5 rounded-md transition-all duration-200 ease-out cursor-pointer hover:bg-white/70 dark:hover:bg-stone-800/60 hover:-translate-y-0.5 hover:shadow-md hover:text-emerald-600 dark:hover:text-emerald-400 data-[state=active]:shadow-lg data-[state=active]:-translate-y-0.5"
            >
              <Store className="h-4 w-4 mr-2 transition-transform duration-200 group-hover:scale-110 group-hover:rotate-[-4deg]" strokeWidth={1.75}/> Local businesses
            </TabsTrigger>
            <TabsTrigger
              value="members"
              data-testid="benefits-tab-members"
              className="group py-2.5 rounded-md transition-all duration-200 ease-out cursor-pointer hover:bg-white/70 dark:hover:bg-stone-800/60 hover:-translate-y-0.5 hover:shadow-md hover:text-emerald-600 dark:hover:text-emerald-400 data-[state=active]:shadow-lg data-[state=active]:-translate-y-0.5"
            >
              <HeartHandshake className="h-4 w-4 mr-2 transition-transform duration-200 group-hover:scale-110 group-hover:rotate-[-4deg]" strokeWidth={1.75}/> Members
            </TabsTrigger>
          </TabsList>

          <TabsContent value="community" data-testid="benefits-content-community">
            <div className="grid md:grid-cols-2 gap-5">
              <BenefitCard icon={MapPin}     title="Every dollar recirculates locally"
                body="Membership keeps commerce onshore. When you order dinner, book a stay or grab a coupon, the transaction runs on Aussie rails — not offshore aggregators taking a 30% clip."/>
              <BenefitCard icon={ScrollText} title="Free directory for every business"
                body="HustleDirectory lists every claimable Australian business with ABN verification and free basic profiles. The community can see who is real."/>
              <BenefitCard icon={Users}      title="Circles, Meetups & Matchmaker"
                body="Real-world community groups — book clubs, sports clubs, alumni, running crews, and human-curated introductions instead of swipe apps."/>
              <BenefitCard icon={MessageSquare} title="Local news without adtech"
                body="HustlePress and HustleOpinions publish suburb-scoped stories with no tracking pixels. HustleReviews keeps businesses honest."/>
              <BenefitCard icon={Plane}     title="Tourism dollars stay onshore"
                body="Visitors land on HustleTravel, HustleStays, HustleBnB, HustleFinder, HustleEats. Money flows to local operators — not Booking.com."
                cta={{ to: "/membership/community/tourism", label: "See the tourism impact →" }}/>
              <BenefitCard icon={HeartHandshake} title="Not-for-profit friendly"
                body="Sporting clubs, schools and charities get free HustleTickets, HustleEvents, HustleCircles and HustleDirectory listings — the tools cost nothing when purpose is community."/>
            </div>
          </TabsContent>

          <TabsContent value="business" data-testid="benefits-content-business">
            <div className="grid md:grid-cols-2 gap-5">
              <BenefitCard icon={Store} title="One back-office for every trade"
                body="HustlePlate for restaurants, HustleShop + HustlePlaza for retail, HustleStays + HustleBnB for accommodation, HustleProperties + HustleDIY for real estate. Every module talks to the same customer, bookings, inventory and payout ledger."/>
              <BenefitCard icon={Wallet} title="Direct-sale margins, not aggregator margins"
                body="Zero commission on direct sales. Optional aggregator listings (Amazon, Trivago, Hotelbeds) are opt-in and clearly labelled — the direct-first customer is always yours."/>
              <BenefitCard icon={MessageSquare} title="HustleCRM: every lead in one inbox"
                body="Booking DMs, review replies, chat threads, waitlists, phone-form fills — all land in one HustleCRM inbox. Reply once, close faster."/>
              <BenefitCard icon={Chrome} title="Keep your WordPress site"
                body="HustleConnect is a free WordPress plugin that wires your existing WP site into the Suite. Bookings, tickets, members — synced in both directions."/>
              <BenefitCard icon={BadgeCheck} title="Portable reviews and reputation"
                body="HustleReviews travel with the business record — a five-star kitchen keeps its five stars if it opens a second venue or lists on HustleStays for a chef-stay."/>
              <BenefitCard icon={Rocket} title="AI press-release engine"
                body="HustlePress writes launch stories, seasonal menu notes and offer round-ups in the tone you set. Publish direct to your suburb's HustlePress feed with one click."/>
            </div>
          </TabsContent>

          <TabsContent value="members" data-testid="benefits-content-members">
            <div className="grid md:grid-cols-2 gap-5">
              <BenefitCard icon={Smartphone} title={`${ALL_MODULES.length}+ apps, one login`}
                body="From ordering dinner (HustleEats) to reading local news (HustlePress) to booking a road-trip (HustleRoadtrip) — one account signs you into everything."/>
              <BenefitCard icon={Wallet} title="Cash-back rewards, not points"
                body="HustleRewards pays you virtual AUD on every Suite purchase. Spend it on a coffee, a hotel night or a concert ticket — never expires, never devalues."/>
              <BenefitCard icon={Ticket} title="Priority access + Founding Member badge"
                body="First dibs on Meetups, sold-out shows on HustleTickets, capped-supply HustleBargains drops. Founding Members carry a badge across every module."/>
              <BenefitCard icon={HeartHandshake} title="Buddy-Pay bill splitting"
                body="Split the dinner, the hotel, the ticket, the tackle order — every payable in the Suite supports one-tap split with any Buddy in your contacts."/>
              <BenefitCard icon={Globe} title="Multilingual by default"
                body="13 languages baked in. Live translate every menu, ticket and property listing so friends and family who don't speak English still buy from local Aussie shops."/>
              <BenefitCard icon={ShieldCheck} title="Free forever, cancel anytime"
                body="Base membership is free. Some paid tiers exist (Pro Rewards, Circles Plus, Property audit) but the core Suite stays free forever. No app store required."/>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <a href="/signup" data-testid="benefits-cta-signup">
            <Button className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold px-7 h-12 text-base">
              Join Hustle Membership — free
              <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
            </Button>
          </a>
          <a href="#modules" data-testid="benefits-cta-modules">
            <Button variant="outline"
              className="rounded-full border-stone-300 dark:border-stone-700 bg-transparent text-stone-800 dark:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 font-semibold px-7 h-12 text-base">
              Browse every module
            </Button>
          </a>
        </div>
      </section>

      {/* Module grid */}
      <section id="modules" className="max-w-6xl mx-auto px-4 sm:px-6 pb-14 lg:pb-20">
        <h2 className="text-base md:text-lg font-bold uppercase tracking-[0.2em] text-emerald-400">
          The modules
        </h2>
        <p className="text-stone-600 dark:text-stone-400 mt-2 mb-8 max-w-2xl">
          Every module is its own product — all connected through one Suite account.
        </p>
        <p className="text-xs text-stone-500 dark:text-stone-500 mb-4 italic">
          Hover any card to flip it for the full story · tap on mobile.
        </p>
        <div data-testid="suite-module-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {ALL_MODULES.map((mod) => {
            const href = mod.href;
            const longDesc = MODULE_LONG_DESC[mod.key] || mod.desc;
            const front = (
              <div className="h-full rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/40 p-5 flex flex-col justify-between">
                <div>
                  <span
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-xs font-extrabold text-stone-950 mb-3"
                    style={{ backgroundColor: mod.color }}
                    aria-hidden="true"
                  >
                    {mod.initials}
                  </span>
                  <h3 className="font-semibold text-sm text-stone-900 dark:text-stone-100">
                    {mod.title}
                  </h3>
                  <p className="text-xs text-stone-700 dark:text-stone-300 mt-1.5 line-clamp-2">
                    {mod.desc}
                  </p>
                </div>
                <p className="mt-3 text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-500">
                  Hover to learn more →
                </p>
              </div>
            );
            const back = (
              <a
                href={href}
                data-testid={`module-card-${mod.key}-cta`}
                rel="noopener"
                className="block h-full rounded-xl p-5 text-left transition-colors overflow-hidden"
                style={{
                  backgroundColor: mod.color + "15",
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: mod.color + "80",
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-extrabold text-stone-950"
                    style={{ backgroundColor: mod.color }}
                    aria-hidden="true"
                  >
                    {mod.initials}
                  </span>
                  <span className="font-semibold text-sm text-stone-900 dark:text-stone-100">
                    {mod.title}
                  </span>
                </div>
                <p className="text-[11px] text-stone-700 dark:text-stone-200 leading-relaxed line-clamp-5">
                  {longDesc}
                </p>
                <span
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold"
                  style={{ color: mod.color }}
                >
                  Visit {mod.title} <ArrowRight className="h-3 w-3" strokeWidth={2} />
                </span>
              </a>
            );
            return (
              <FlipCard
                key={mod.key}
                testid={`module-card-${mod.key}`}
                height={200}
                front={front}
                back={back}
              />
            );
          })}
        </div>
      </section>

      {/* HustleSuite Assistant — Chrome extension for the whole Suite.
       *  Landed. Deep-links to the dedicated sales page /hustlesuite-assistant.
       *
       *  iter268d: section bg is ALWAYS dark (`from-emerald-950/30 via-
       *  stone-950 to-stone-950`) — so text uses always-light colours,
       *  NOT `dark:`-prefixed pairs. Otherwise the H2 vanished in
       *  light mode (dark-on-dark).
       */}
      <section
        id="assistant"
        data-testid="suite-assistant-strip"
        className="border-t border-stone-200 dark:border-stone-800 bg-gradient-to-br from-emerald-950/30 via-stone-950 to-stone-950
                   px-4 sm:px-6 py-14 lg:py-18"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_auto] items-center gap-8">
          <div>
            <p className="text-emerald-400 text-xs font-bold uppercase tracking-[0.28em] flex items-center gap-2">
              <Chrome className="h-3.5 w-3.5" strokeWidth={2}/>
              Now shipping · Chrome extension
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight max-w-3xl text-white">
              HustleSuite Assistant — <span className="text-emerald-400">every</span> tab, powered by your Suite.
            </h2>
            <p className="mt-4 text-stone-300 leading-relaxed max-w-2xl">
              Voice-to-text on any form, edit your WordPress site inline, save any page into the right module,
              see our prices on Amazon / eBay / Kmart, and import Google reviews to your HustleFinder listing —
              all from one free Chrome extension. 13 languages.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/hustlesuite-assistant" data-testid="suite-assistant-learn-more">
                <Button className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold px-6 h-11">
                  See what the Assistant does <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
                </Button>
              </Link>
              <a href="https://chrome.google.com/webstore/detail/hustlesuite-assistant/"
                 target="_blank" rel="noreferrer"
                 data-testid="suite-assistant-install">
                <Button variant="outline"
                  className="rounded-full border-stone-500 bg-transparent text-white hover:bg-white hover:text-stone-900 hover:border-white px-6 h-11 transition-colors">
                  <Chrome className="h-4 w-4 mr-2" strokeWidth={2} /> Add to Chrome
                </Button>
              </a>
            </div>
          </div>
          <div className="hidden lg:flex items-center justify-center">
            <div className="grid grid-cols-3 gap-2 max-w-[280px]">
              {[Mic, Edit3, Bookmark, Search, MessageSquare, LayoutDashboard].map((Icon, i) => (
                <div key={i}
                     className="aspect-square rounded-xl border border-emerald-800/60 bg-emerald-500/10
                                flex items-center justify-center">
                  <Icon className="h-6 w-6 text-emerald-400" strokeWidth={1.75}/>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap — 6 upcoming Chrome-extension tools. Shares the same
       *  i18n keys (plaza_roadmap_*) that power the HustlePlaza sales
       *  page so translations don't diverge.
       *
       *  iter268d: section bg is forced-dark (`from-stone-950 to-
       *  stone-900/60`) so headings and body use ALWAYS-light colours.
       *  Previously the H2 had NO text colour → inherited dark from
       *  body in light mode → invisible on the dark gradient.
       */}
      <section
        id="roadmap"
        data-testid="suite-roadmap"
        className="border-t border-stone-200 dark:border-stone-800 bg-gradient-to-b from-stone-950 to-stone-900/60 px-4 sm:px-6 py-16 lg:py-20"
      >
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl">
            <p className="text-emerald-400 text-xs font-bold uppercase tracking-[0.28em] flex items-center gap-2">
              <Rocket className="h-3.5 w-3.5" strokeWidth={2}/>
              {t("plaza_roadmap_kicker", { defaultValue: "What we're building next" })}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
              {t("plaza_roadmap_title", { defaultValue: "Six tools built to take work off your plate." })}
            </h2>
            <p className="mt-4 text-stone-300 leading-relaxed">
              {t("plaza_roadmap_sub", { defaultValue: "HustleSuite is more than a shop — it is a full productivity layer. Here is what is on the runway." })}
            </p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4"
                data-testid="suite-roadmap-soon">
            {[
              ["plaza_roadmap_1_t", "plaza_roadmap_1_b", Mic],
              ["plaza_roadmap_2_t", "plaza_roadmap_2_b", Edit3],
              ["plaza_roadmap_3_t", "plaza_roadmap_3_b", Bookmark],
            ].map(([tk, bk, Icon]) => (
              <SuiteRoadmapCard key={tk} t={t} titleKey={tk} bodyKey={bk} Icon={Icon}
                     badge={t("plaza_roadmap_soon", { defaultValue: "Shipping soon" })}
                     badgeTone="soon" />
            ))}
          </div>
          <div className="mt-4 grid md:grid-cols-3 gap-4"
                data-testid="suite-roadmap-next">
            {[
              ["plaza_roadmap_4_t", "plaza_roadmap_4_b", Search],
              ["plaza_roadmap_5_t", "plaza_roadmap_5_b", MessageSquare],
              ["plaza_roadmap_6_t", "plaza_roadmap_6_b", LayoutDashboard],
            ].map(([tk, bk, Icon]) => (
              <SuiteRoadmapCard key={tk} t={t} titleKey={tk} bodyKey={bk} Icon={Icon}
                     badge={t("plaza_roadmap_next", { defaultValue: "Next in the queue" })}
                     badgeTone="next" />
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="enquire" className="border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
              Bring your business into the Suite.
            </h2>
            <p className="text-stone-600 dark:text-stone-400 mt-4 max-w-md">
              Restaurants, retailers, hosts, organisers — tell us what you do
              and we&apos;ll show you where you fit. Real people, Australian time zones.
            </p>
            {copy.contact_email && (
              <p className="text-sm text-stone-500 dark:text-stone-500 mt-6">
                Or email us directly:{" "}
                <a href={`mailto:${copy.contact_email}`} className="text-emerald-400 hover:underline">
                  {copy.contact_email}
                </a>
              </p>
            )}
          </div>
          <div className="rounded-xl border border-stone-200 bg-white p-6"><PublicFormView inline formSlug="hustlesuite-enquiry" sourceUrl={window.location.origin+window.location.pathname} /></div>
        </div>
      </section>

      {/* Footer — shared SuiteFooter */}
      <SuiteFooter />
    </div>
  );
}

/** Individual roadmap card — dark theme variant matching the Suite
 *  sales page. Same i18n keys as HustlePlaza's roadmap.
 *
 *  iter268c: badge tone now carries an explicit LIGHT-mode palette
 *  (bg-emerald-100 / text-emerald-900) with a dark: variant. The
 *  wrapper section is ALWAYS gradient-dark, but the card body is
 *  `bg-white dark:bg-stone-950/60` — so in light mode the badge sat
 *  on a WHITE card and the previous `bg-emerald-900/40 + text-emerald-300`
 *  read as low-contrast washed-out pastel.
 */
function SuiteRoadmapCard({ t, titleKey, bodyKey, Icon, badge, badgeTone }) {
  const tone = badgeTone === "soon"
    ? "bg-emerald-100 text-emerald-900 border-emerald-400 dark:bg-emerald-900/60 dark:text-emerald-100 dark:border-emerald-700"
    : "bg-amber-100 text-amber-900 border-amber-400 dark:bg-amber-900/60 dark:text-amber-100 dark:border-amber-700";
  return (
    <div className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950/60 p-6 hover:border-emerald-700/60 transition-colors flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
          <Icon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.75}/>
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full border ${tone}`}>
          {badge}
        </span>
      </div>
      <h3 className="font-bold text-base text-stone-900 dark:text-stone-100 leading-snug">
        {t(titleKey)}
      </h3>
      <p className="text-sm text-stone-700 dark:text-stone-400 mt-2 leading-relaxed flex-1">
        {t(bodyKey)}
      </p>
    </div>
  );
}

/** Benefit tile inside the audience-tabs section. Icon-topped, one-line
 *  bold title, 2-3 sentence body, optional CTA link. Kept as a local
 *  helper (not exported) — it's only used on this page. */
function BenefitCard({ icon: Icon, title, body, cta }) {
  return (
    <div
      data-testid={`benefit-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/40 p-6 hover:border-emerald-500/50 transition-colors"
    >
      <Icon className="h-5 w-5 text-emerald-500 mb-3" strokeWidth={1.5}/>
      <h3 className="font-bold text-base text-stone-900 dark:text-stone-100 leading-snug">
        {title}
      </h3>
      <p className="text-sm text-stone-600 dark:text-stone-400 mt-2 leading-relaxed">
        {body}
      </p>
      {cta && (
        <Link
          to={cta.to}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-500"
        >
          {cta.label}
        </Link>
      )}
    </div>
  );
}
